import API from '@/shared/plugins/Api/API'
import { readBlobError } from '@/shared/plugins/Api/EventExportHelpers'

// Both calls can take many minutes: the server streams the export, and gives an import
// up to 30 min to arrive before it runs it. Neither is retried: a retry would build the
// export again, or send the archive again and replay a WIPE.
const LONG_REQUEST = { retries: 0, timeout: 60 * 60 * 1000 }

class BackupV3 extends API {
  constructor (id) {
    super(id)
    this.prefix = '/public/v1'
  }

  // Returns the axios response: `data` is the zip as a Blob.
  async export (body, { onDownloadProgress, cancelToken } = {}) {
    try {
      return await this.request({
        ...LONG_REQUEST,
        method: 'post',
        url: `${this.prefix}/backup/export`,
        data: body,
        responseType: 'blob',
        originalResponse: true,
        onDownloadProgress,
        cancelToken
      })
    } catch (err) {
      throw await readBlobError(err)
    }
  }

  // Sends the whole archive and returns the import report. Every field is sent, since
  // the server defaults (WIPE, SQLite and backups imported) must never apply by accident.
  async import (file, { mode, includeSqlite, includeSqliteBackups }, { onUploadProgress } = {}) {
    const formData = new FormData()
    formData.append('mode', mode)
    formData.append('includeSqlite', String(includeSqlite))
    formData.append('includeSqliteBackups', String(includeSqliteBackups))
    // Last, after the text fields: multer fills the body in the order the parts arrive.
    formData.append('archive', file)
    const report = await this.request({
      ...LONG_REQUEST,
      method: 'post',
      url: `${this.prefix}/backup/import`,
      data: formData,
      onUploadProgress
    })
    // The server answers 200 at once and sends the report at the end. Without it, the
    // import may still be running: the caller must not report a success or a failure.
    if (!Array.isArray(report?.applications)) {
      const err = new Error('BackupImportNoReport')
      err.code = 'BackupImportNoReport'
      throw err
    }
    return report
  }
}

export default BackupV3
